/**
 * The media formats a notification's demo video / poster image may use.
 *
 * ONE source for a set that was previously written twice, in two shapes: an
 * `accept` attribute string on the file input (`.mp4,.webm`) and a flat
 * extension array in the upload client's pre-flight check. Adding a format
 * meant editing both, in different files, with nothing to fail if you missed
 * one — and the two failure modes point opposite ways. Miss the `accept` and
 * the file picker won't offer a format the API accepts; miss the array and the
 * picker offers one the client then rejects.
 *
 * These mirror the backend's `ALLOWED_MEDIA_TYPES`
 * (legal-ai-backend `utils/notifications/product-asset-media.ts`), which is the
 * authority — it is what actually signs the upload and rejects a bad
 * content-type. There is no build-time link between the repos, so a format
 * added there must be added here too; the pairing is pinned by a test.
 */

/** Video container extensions, without the dot. */
export const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'webm'] as const;

/** Still-image extensions, without the dot. */
export const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif'] as const;

/** Every extension the media-upload endpoint will sign a URL for. */
export const ALLOWED_MEDIA_EXTENSIONS: readonly string[] = [
  ...ALLOWED_VIDEO_EXTENSIONS,
  ...ALLOWED_IMAGE_EXTENSIONS,
];

/**
 * Build the `accept` attribute for a file input from the same list the
 * validation uses, so the picker and the check can never disagree.
 */
export const acceptAttribute = (extensions: readonly string[]): string =>
  extensions.map((ext) => `.${ext}`).join(',');

export const VIDEO_ACCEPT = acceptAttribute(ALLOWED_VIDEO_EXTENSIONS);
export const IMAGE_ACCEPT = acceptAttribute(ALLOWED_IMAGE_EXTENSIONS);

/** The path prefix every upload-produced media reference carries. */
export const MEDIA_PATH_PREFIX = '/notifications/media/';

/**
 * The ONLY shape a stored VIDEO reference may take —
 * `/notifications/media/<uuid>.<mp4|webm>`.
 *
 * Mirrors the backend's `MEDIA_PATH_PATTERN`
 * (legal-ai-backend `utils/notifications/product-asset-media.ts`), which is the
 * authority — it is what the upload endpoint's Joi schema enforces and what the
 * playback route turns back into an S3 key.
 *
 * A prefix check is NOT enough, and the difference is a real hole: the browser
 * normalises `..` segments before issuing the request, so a stored
 * `/notifications/media/../../admin/notifications` would join to the API origin
 * and then resolve to `https://<api>/admin/notifications` — a cross-path GET
 * fired automatically on render. Anchoring the id and the extension makes that
 * unrepresentable rather than merely unlikely.
 *
 * Built by interpolating ALLOWED_VIDEO_EXTENSIONS rather than re-typing the
 * formats, for the same reason the accept strings are: a second list drifts,
 * and this one drifting silently refuses a format the backend accepts.
 */
export const VIDEO_MEDIA_PATH_PATTERN = new RegExp(
  `^${MEDIA_PATH_PREFIX}[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}` +
    `\\.(${ALLOWED_VIDEO_EXTENSIONS.join('|')})$`,
);

/** True when `value` is an upload-produced video path of exactly the shape above. */
export const isVideoMediaPath = (value?: string): boolean =>
  !!value && VIDEO_MEDIA_PATH_PATTERN.test(value);
